import React from "react";
import { History, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { AUDIT_FIELD_LABELS } from "@/lib/audit/documentAudit";
import {
  DocumentVersion,
  VersionTarget,
  diffFields,
  fetchCurrentRecord,
  fetchDocumentVersions,
  restoreDocumentVersion,
} from "@/lib/documents/versions";

const formatDate = (value: string) =>
  new Date(value).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });

interface Props {
  target: VersionTarget;
  onRestored?: () => void | Promise<void>;
  /** Visa som liten knapp i en lista i stället för i dokumenthuvudet */
  compact?: boolean;
}

/** Visar tidigare versioner av dokumentet och låter användaren återställa en. */
export const VersionHistoryDialog: React.FC<Props> = ({ target, onRestored, compact }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [versions, setVersions] = React.useState<DocumentVersion[]>([]);
  const [current, setCurrent] = React.useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [rows, currentRow] = await Promise.all([
        fetchDocumentVersions(target),
        fetchCurrentRecord(target),
      ]);
      setVersions(rows);
      setCurrent(currentRow);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.type, target.id]);

  React.useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const restore = async (version: DocumentVersion) => {
    setBusy(version.id);
    try {
      await restoreDocumentVersion(target, version);
      toast.success(`Version ${version.version} är återställd`, {
        description: "Det tidigare innehållet sparades som en ny version.",
      });
      await load();
      await onRestored?.();
    } catch (e) {
      toast.error("Kunde inte återställa versionen", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <History className="h-4 w-4" />
          {compact ? "Versioner" : "Versionshistorik"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Versionshistorik{target.name ? ` – ${target.name}` : ""}</DialogTitle>
          <DialogDescription>
            Varje sparning skapar en version. Du kan titta på en tidigare version och återställa
            den – ingenting skrivs över, den nuvarande texten sparas som en ny version.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          {loading && (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Hämtar versioner…
            </p>
          )}

          {!loading && versions.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              Inga tidigare versioner ännu. Nästa gång dokumentet ändras sparas en version här.
            </p>
          )}

          <ul className="space-y-3">
            {versions.map((v) => {
              const changed = diffFields(v.snapshot, current);
              return (
                <li key={v.id} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">Version {v.version}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {v.actor_name || "okänd användare"}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(v.created_at)}</span>
                  </div>

                  <p className="mt-2 text-xs text-muted-foreground">
                    {changed.length === 0
                      ? "Innehållet är detsamma som nu."
                      : `Skiljer sig i: ${changed
                          .map((f) => AUDIT_FIELD_LABELS[f] ?? f)
                          .slice(0, 6)
                          .join(", ")}${changed.length > 6 ? " m.fl." : ""}`}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                    >
                      {expanded === v.id ? "Dölj innehåll" : "Visa innehåll"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy === v.id}
                      onClick={() => restore(v)}
                    >
                      {busy === v.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="mr-2 h-4 w-4" />
                      )}
                      Återställ den här versionen
                    </Button>
                  </div>

                  {expanded === v.id && (
                    <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground">
                      {JSON.stringify(v.snapshot, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryDialog;
